import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { formatPrice } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['hsl(135, 25%, 45%)', 'hsl(135, 20%, 75%)', 'hsl(30, 30%, 85%)', 'hsl(140, 50%, 45%)', 'hsl(40, 95%, 55%)', 'hsl(0, 65%, 55%)'];

export const AdminAnalytics = () => {
  const { token } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [productsRes, categoriesRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/products?status=`),
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/orders`, { headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` } })
      ]);

      const products = productsRes.data;
      const categories = categoriesRes.data;
      const orders = ordersRes.data;

      // Products by category
      const productsByCategory = categories.map(cat => ({
        name: cat.name,
        count: products.filter(p => p.category_id === cat.id).length
      }));

      // Orders by status
      const ordersByStatus = [
        { name: 'Pending', count: orders.filter(o => o.status === 'pending').length },
        { name: 'Confirmed', count: orders.filter(o => o.status === 'confirmed').length },
        { name: 'Shipped', count: orders.filter(o => o.status === 'shipped').length },
        { name: 'Delivered', count: orders.filter(o => o.status === 'delivered').length },
        { name: 'Canceled', count: orders.filter(o => o.status === 'canceled').length }
      ].filter(item => item.count > 0);

      // Revenue over time (mock data for last 7 days)
      const revenueData = [
        { day: 'Mon', revenue: 1200 },
        { day: 'Tue', revenue: 1800 },
        { day: 'Wed', revenue: 1500 },
        { day: 'Thu', revenue: 2200 },
        { day: 'Fri', revenue: 2800 },
        { day: 'Sat', revenue: 3200 },
        { day: 'Sun', revenue: 2600 }
      ];

      // Top selling products
      const topProducts = products
        .sort((a, b) => (b.reviews_count || 0) - (a.reviews_count || 0))
        .slice(0, 5)
        .map(p => ({
          name: p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name,
          sales: Math.floor(Math.random() * 100) + 50 // Mock sales data
        }));

      setAnalyticsData({
        productsByCategory,
        ordersByStatus,
        revenueData,
        topProducts
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analyticsData) {
    return <div className="text-center text-muted-foreground">No analytics data available</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl font-bold">Analytics & Insights</h2>

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Products by Category */}
        <Card>
          <CardHeader>
            <CardTitle>Products by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.productsByCategory}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Orders by Status */}
        <Card>
          <CardHeader>
            <CardTitle>Orders by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.ordersByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {analyticsData.ordersByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Revenue Over Time */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" />
                <YAxis tickFormatter={(value) => `$${value}`} />
                <Tooltip 
                  formatter={(value) => formatPrice(value)}
                  contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={3}
                  dot={{ fill: 'hsl(var(--primary))', r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Selling Products */}
        <Card>
          <CardHeader>
            <CardTitle>Top Selling Products</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.topProducts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Bar dataKey="sales" fill="hsl(var(--success))" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};